/**
 * Node ID Mappings for Migration
 *
 * This file contains all node ID mappings between the old (cloudflare) branch
 * and the new (main) branch structure. Separated for easy maintenance and tracking.
 *
 * Total changes: 27 nodes renamed across all segments
 */

// Segment 1: Opening and initial player choices
export const SEGMENT_1_MAPPINGS = {
  Node1_Setup: 'S1_Opening_Hook',
  Node1_PathA: 'S1_Reaction_A',
  Node1_PathB: 'S1_Reaction_B',
  Node1_PathC: 'S1_Reaction_C',
  Node2_Main_Content: 'S1_Player_Declaration',
} as const;

// Segment 2: B-Plot diversion
export const SEGMENT_2_MAPPINGS = {
  Node3_Surprising_Shift: 'S2_BPlot_Intro',
  BPlot_Gauntlet_Path_A_Stage1: 'S2A_BPlot_Engage',
  BPlot_Gauntlet_Path_A_Stage2: 'S2A_BPlot_Reflect',
  BPlot_Gauntlet_Path_B_Stage1: 'S2B_BPlot_Engage',
  BPlot_Gauntlet_Path_B_Stage2: 'S2B_BPlot_Reflect',
  BPlot_Gauntlet_Path_C_Stage1: 'S2C_BPlot_Engage',
  BPlot_Gauntlet_Path_C_Stage2: 'S2C_BPlot_Reflect',
  Return_To_A_Plot_Player_Led: 'S2_Return_To_A_Plot_Player_Led',
} as const;

// Segment 3: Parallel world branches
export const SEGMENT_3_MAPPINGS = {
  // Memory/Recall nodes
  FinalSequence_Memory_Node: 'S3A_NPC_Recall_Positive', // Default to Better World
  'NPC_Returns_To_A_Plot_variant-good': 'S3A_NPC_Recall_Positive',
  'NPC_Returns_To_A_Plot_variant-bad': 'S3B_NPC_Recall_Negative',

  // Silent intensify nodes
  'In_Silence_Intensify_6a_variant-good': 'S3A_Silent_Intensify_A',
  'In_Silence_Intensify_6b_variant-good': 'S3A_Silent_Intensify_B',
  'In_Silence_Intensify_6a_variant-bad': 'S3B_Silent_Intensify_A',
  'In_Silence_Intensify_6b_variant-bad': 'S3B_Silent_Intensify_B',

  // Ending nodes
  'Ending_A_variant-good': 'S3A_Ending_A',
  'Ending_B_variant-good': 'S3A_Ending_B',
  'Ending_C_variant-good': 'S3A_Ending_C',
  'Ending_D_variant-good': 'S3A_Ending_D',
  'Ending_A_variant-bad': 'S3B_Ending_A',
  'Ending_B_variant-bad': 'S3B_Ending_B',
  'Ending_C_variant-bad': 'S3B_Ending_C',
  'Ending_D_variant-bad': 'S3B_Ending_D',
} as const;

// Complete mapping combining all segments
export const NODE_ID_MAPPINGS = {
  ...SEGMENT_1_MAPPINGS,
  ...SEGMENT_2_MAPPINGS,
  ...SEGMENT_3_MAPPINGS,
} as const;

// Legacy nodes to be removed (no mapping, just deletion)
export const LEGACY_NODES_TO_REMOVE = [
  'GoodEnding1',
  'GoodEnding2',
  'BadEnding1',
  'BadEnding2',
  'GoodEpilogue',
  'BadEpilogue',
  'END_OF_NARRATIVE',
] as const;

// Type definitions for better type safety
export type OldNodeId = keyof typeof NODE_ID_MAPPINGS;
export type NewNodeId = (typeof NODE_ID_MAPPINGS)[OldNodeId];
export type LegacyNodeId = (typeof LEGACY_NODES_TO_REMOVE)[number];

// Helper functions
export function getNewNodeId(oldId: string): string | undefined {
  return NODE_ID_MAPPINGS[oldId as OldNodeId];
}

export function isLegacyNode(nodeId: string): boolean {
  return LEGACY_NODES_TO_REMOVE.includes(nodeId as LegacyNodeId);
}

export function shouldMigrateNode(nodeId: string): boolean {
  return nodeId in NODE_ID_MAPPINGS;
}

// Reverse mapping for debugging/rollback
export const REVERSE_NODE_ID_MAPPINGS = Object.entries(NODE_ID_MAPPINGS).reduce(
  (acc, [oldId, newId]) => {
    acc[newId] = oldId;
    return acc;
  },
  {} as Record<string, string>
);

// Statistics for tracking
export const MIGRATION_STATS = {
  segment1Count: Object.keys(SEGMENT_1_MAPPINGS).length,
  segment2Count: Object.keys(SEGMENT_2_MAPPINGS).length,
  segment3Count: Object.keys(SEGMENT_3_MAPPINGS).length,
  totalMappings: Object.keys(NODE_ID_MAPPINGS).length,
  legacyRemovals: LEGACY_NODES_TO_REMOVE.length,
  totalChanges: Object.keys(NODE_ID_MAPPINGS).length + LEGACY_NODES_TO_REMOVE.length,
};
